import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { extractErrorMessage } from '@/queries/ticketMutations'
import { useSaveSlaSettings, useSupportAdmins } from '@/queries/settings'
import {
  COMMON_TIMEZONES,
  MAX_TARGET_VALUE,
  SEVERITIES,
  SLA_TARGETS,
  WEEKDAYS,
  holidayLabels,
  severityDefinitions,
  severityLabels,
  slaFormErrors,
  slaTargetLabels
} from '@/lib/sla'
import type { CompanySla, HolidayRegion, SlaTarget, TargetUnit } from '@/types/sla'
import type { Severity } from '@/types/ticket'

const selectClass =
  'h-[var(--etn-control-height-input)] rounded-etn-menu border border-etn-border-input bg-etn-surface px-3 text-etn-control text-etn-text-cell'

const HOLIDAY_REGIONS = Object.keys(holidayLabels) as HolidayRegion[]

export interface Editing {
  companyId: string
  name: string
  sla: CompanySla
}

// Target values stay strings while being typed, so clearing a box doesn't snap it to 0.
type TargetDrafts = Record<Severity, Record<SlaTarget, { value: string; unit: TargetUnit }>>

function toDrafts(targets: CompanySla['targets']): TargetDrafts {
  return Object.fromEntries(
    SEVERITIES.map(s => [s, Object.fromEntries(SLA_TARGETS.map(t => [t, { value: String(targets[s][t].value), unit: targets[s][t].unit }]))])
  ) as TargetDrafts
}

function fromDrafts(drafts: TargetDrafts): CompanySla['targets'] {
  return Object.fromEntries(
    SEVERITIES.map(s => [
      s,
      Object.fromEntries(
        SLA_TARGETS.map(t => [t, { value: drafts[s][t].value.trim() === '' ? NaN : Number(drafts[s][t].value), unit: drafts[s][t].unit }])
      )
    ])
  ) as CompanySla['targets']
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className="text-etn-micro text-etn-danger-text">{message}</p>
}

export function SlaEditor({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const [enabled, setEnabled] = useState(editing.sla.enabled)
  const [primaryContactId, setPrimaryContactId] = useState(editing.sla.primaryContactId)
  const [hours, setHours] = useState(editing.sla.businessHours)
  const [targets, setTargets] = useState(() => toDrafts(editing.sla.targets))
  const [startDate, setStartDate] = useState(editing.sla.startDate)
  const [endDate, setEndDate] = useState(editing.sla.endDate)
  const [error, setError] = useState<string | null>(null)
  const admins = useSupportAdmins(editing.sla.primaryContactId)
  const save = useSaveSlaSettings()

  const sla: CompanySla = { enabled, primaryContactId, businessHours: hours, targets: fromDrafts(targets), startDate, endDate }
  const errors = slaFormErrors(sla)
  const invalid = Object.keys(errors).length > 0
  const timezones = COMMON_TIMEZONES.includes(hours.timezone) ? COMMON_TIMEZONES : [hours.timezone, ...COMMON_TIMEZONES]

  function toggleDay(day: number, checked: boolean) {
    setHours(h => ({ ...h, days: checked ? [...h.days, day].sort((a, b) => a - b) : h.days.filter(d => d !== day) }))
  }

  function setTarget(severity: Severity, target: SlaTarget, patch: Partial<{ value: string; unit: TargetUnit }>) {
    setTargets(prev => ({ ...prev, [severity]: { ...prev[severity], [target]: { ...prev[severity][target], ...patch } } }))
  }

  async function handleSave() {
    setError(null)
    try {
      await save.mutateAsync({ companyId: editing.companyId, sla })
      toast.success(`SLAs saved for ${editing.name}`)
      onClose()
    } catch (err) {
      setError(extractErrorMessage(err, 'Could not save the SLAs'))
    }
  }

  return (
    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>SLAs for {editing.name}</DialogTitle>
        <DialogDescription>Targets count business hours only. Tickets already open keep the policy they started with.</DialogDescription>
      </DialogHeader>

      <div className="space-y-5">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="h-4 w-4" />
          SLAs on for this company
        </label>

        <div className="space-y-2">
          <Label htmlFor="sla-contact">Primary contact</Label>
          <select
            id="sla-contact"
            className={`${selectClass} w-full`}
            value={primaryContactId ?? ''}
            disabled={admins.isLoading}
            onChange={e => setPrimaryContactId(e.target.value || null)}
            aria-invalid={Boolean(errors.primaryContactId)}
          >
            <option value="">No primary contact</option>
            {admins.data?.data.map(a => (
              <option key={a._id} value={a._id}>
                {a.name ?? a.email ?? a._id}
              </option>
            ))}
          </select>
          <FieldError message={errors.primaryContactId} />
        </div>

        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm font-semibold">Contract term</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="sla-start-date">Start date</Label>
              <Input
                id="sla-start-date"
                type="date"
                value={startDate ?? ''}
                onChange={e => setStartDate(e.target.value || null)}
                aria-invalid={Boolean(errors.term)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sla-end-date">End date</Label>
              <Input id="sla-end-date" type="date" value={endDate ?? ''} onChange={e => setEndDate(e.target.value || null)} aria-invalid={Boolean(errors.term)} />
            </div>
          </div>
          <FieldError message={errors.term} />
          <p className="text-etn-micro text-muted-foreground">
            From the Term in the client&apos;s main agreement. For display only: the renewal is flagged on the client dashboard from a month before
            the end date, and the dates never switch SLAs off.
          </p>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="mb-2 text-sm font-semibold">Business hours</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {WEEKDAYS.map(day => (
              <label key={day.value} className="inline-flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={hours.days.includes(day.value)}
                  onChange={e => toggleDay(day.value, e.target.checked)}
                  className="h-4 w-4"
                />
                {day.label}
              </label>
            ))}
          </div>
          <FieldError message={errors.days} />

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="sla-start">Start</Label>
              <Input
                id="sla-start"
                type="time"
                value={hours.start}
                onChange={e => setHours(h => ({ ...h, start: e.target.value }))}
                aria-invalid={Boolean(errors.hours)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sla-end">End</Label>
              <Input
                id="sla-end"
                type="time"
                value={hours.end}
                onChange={e => setHours(h => ({ ...h, end: e.target.value }))}
                aria-invalid={Boolean(errors.hours)}
              />
            </div>
          </div>
          <FieldError message={errors.hours} />

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="sla-timezone">Timezone</Label>
              <select
                id="sla-timezone"
                className={`${selectClass} w-full`}
                value={hours.timezone}
                onChange={e => setHours(h => ({ ...h, timezone: e.target.value }))}
              >
                {timezones.map(tz => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sla-holidays">Bank holidays</Label>
              <select
                id="sla-holidays"
                className={`${selectClass} w-full`}
                value={hours.holidays}
                onChange={e => setHours(h => ({ ...h, holidays: e.target.value as HolidayRegion }))}
              >
                {HOLIDAY_REGIONS.map(r => (
                  <option key={r} value={r}>
                    {holidayLabels[r]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-2 text-sm font-semibold">Targets</legend>
          <table className="w-full text-left">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th scope="col" className="pb-2 pr-3 font-normal">
                  Severity
                </th>
                {SLA_TARGETS.map(t => (
                  <th key={t} scope="col" className="pb-2 pr-3 font-normal">
                    {slaTargetLabels[t]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SEVERITIES.map(severity => (
                <tr key={severity} className="align-top">
                  <th scope="row" className="py-1.5 pr-3 text-sm font-normal text-foreground">
                    {severityLabels[severity]}
                    <span className="block max-w-[16rem] text-etn-micro text-muted-foreground">{severityDefinitions[severity]}</span>
                    <FieldError message={errors[severity]} />
                  </th>
                  {SLA_TARGETS.map(target => (
                    <td key={target} className="py-1.5 pr-3">
                      <div className="flex items-center gap-1.5">
                        <Input
                          type="number"
                          min={0.25}
                          max={MAX_TARGET_VALUE}
                          step={0.25}
                          aria-label={`${severityLabels[severity]} ${target} target`}
                          value={targets[severity][target].value}
                          onChange={e => setTarget(severity, target, { value: e.target.value })}
                          aria-invalid={Boolean(errors[severity])}
                          className="w-20"
                        />
                        <select
                          aria-label={`${severityLabels[severity]} ${target} unit`}
                          className={selectClass}
                          value={targets[severity][target].unit}
                          onChange={e => setTarget(severity, target, { unit: e.target.value as TargetUnit })}
                        >
                          <option value="hours">hours</option>
                          <option value="days">days</option>
                        </select>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </fieldset>

        {error && (
          <p role="alert" className="text-sm text-etn-danger-text">
            {error}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSave} disabled={save.isPending || invalid}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}
